<template>
  <CustomModal
    :id="id"
    modalClass="custom-modal_no-padding modal-ai-setup"
    :clickToClose="true"
    @onBeforeOpen="onOpened"
    @onClosed="onClosed"
  >
    <template #header="{ hide }">
      <div class="modal-ai-setup__header">
        <BaseHeading
          level="3"
          color="monochrome-07"
          class="modal-ai-setup__title"
        >
          AI Set Up
        </BaseHeading>
        <button
          type="button"
          class="modal-ai-setup__close"
          aria-label="Close"
          @click="hide()"
        >
          <icon
            data="@icon/close.svg"
            :fill="false"
            width="var(--icon-24)"
            height="var(--icon-24)"
          />
        </button>
      </div>
    </template>

    <template #body>
      <div class="modal-ai-setup__content">
        <ModalAiSetupNameSection
          v-if="currentStep === 1"
          :aiName="aiSetupData.name"
          :gender="aiSetupData.gender"
          :genderOptions="genderOptions"
          @update:aiName="onAiNameChange"
          @update:gender="onGenderChange"
        />
        <ModalAiSetupAvatarSection
          v-else-if="currentStep === 2"
          :selectedAvatar="aiSetupData.picture.pictureUrl"
          :aiName="aiSetupData.name"
          @goPrev="goPrevAvatar"
          @goNext="goNextAvatar"
        />
        <ModalAiSetupConfigSection
          v-else
          :providers="providers"
          :models="llmModels"
          :selectedProvider="currentProviderOption"
          :selectedModel="aiSetupData.model"
          :apiKey="aiSetupData.apiKey"
          :apiKeyErrorMessage="apiKeyErrorMessage"
          @update:provider="onSelectProvider"
          @update:model="onSelectModel"
          @update:apiKey="onApiKeyChange"
        />
      </div>
    </template>

    <template #footer="{ validate, hide }">
      <div class="modal-ai-setup__footer">
        <ControlButtons
          :actionDisabled="actionDisabled"
          :hideCancel="currentStep === 1"
          class="modal-ai-setup__control-buttons"
          @cancel="onBack"
          @save="onAction(validate, hide)"
        >
          <template #cancel> Back </template>
          <template #save>
            {{ isFinalStep ? "Save" : "Next" }}
          </template>
        </ControlButtons>
      </div>
    </template>
  </CustomModal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { useStore } from "vuex"

import ModalAiSetupAvatarSection from "@/components/AiSetup/ModalAiSetupAvatarSection.vue"
import ModalAiSetupConfigSection from "@/components/AiSetup/ModalAiSetupConfigSection.vue"
import ModalAiSetupNameSection from "@/components/AiSetup/ModalAiSetupNameSection.vue"
import BaseHeading from "@/components/Base/BaseHeading.vue"
import ControlButtons from "@/components/ControlButtons/index.vue"
import CustomModal from "@/components/Modal/index.vue"

import { s3_base_url } from "@/app.config"
import { AIConfigCurrent, AIConfigModels, AIConfigProvider, AiGender, aiGenders } from "@/models"
import { Actions, Getters } from "@/store"
import { shakeAnimation } from "@/utils/gsapAnimation"

defineProps({
  id: {
    type: String,
    default: "modal-ai-setup"
  }
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const emit = defineEmits<{
  (e: "close"): void
  (
    e: "next",
    payload: { name: string; gender: string; avatarImage: string; config: AIConfigCurrent }
  ): void
}>()

let aiSetupData = ref(new AIConfigCurrent())

const store = useStore()
const apiKeyErrorMessage = ref("")
const isSaving = ref(false)

const maxNumberOfAvatars = 180
let currentAvatarIndex = ref(1)
let avatarHistory = ref<number[]>([])
let avatarHistoryPosition = ref(-1)
const genderOptions = aiGenders

const currentStep = ref(1)

const llmModels = computed(() => {
  if (!providers.value.length) return []
  const provider = providers.value.find(p => p.provider === aiSetupData.value.provider)
  return (provider ?? providers.value[0]).models.filter(model => model.enabled)
})

const providers = computed((): AIConfigProvider[] => store.getters[Getters.GET_AI_CONFIG].providers)

const currentAiConfig = computed(
  (): AIConfigCurrent => store.getters[Getters.GET_AI_CONFIG].current
)

const currentProviderOption = computed(() => {
  return providers.value.find(p => p.provider === aiSetupData.value.provider) || null
})

const isFinalStep = computed(() => currentStep.value === 3)
const actionDisabled = computed(() => isFinalStep.value && isSaving.value)

const resetData = () => {
  aiSetupData.value = new AIConfigCurrent()
  apiKeyErrorMessage.value = ""
  isSaving.value = false
  currentAvatarIndex.value = 1
  avatarHistory.value = []
  avatarHistoryPosition.value = -1
  currentStep.value = 1
}

const onAiNameChange = (value: string) => {
  aiSetupData.value.name = value
}

const onGenderChange = (value: AiGender) => {
  aiSetupData.value.gender = value
  avatarHistory.value = []
  avatarHistoryPosition.value = -1
  currentAvatarIndex.value = 1
}

const onBack = () => {
  if (currentStep.value > 1) currentStep.value -= 1
}

const onStepName = async (validate: () => Promise<{ valid: boolean }>) => {
  const { valid } = await validate()
  if (!valid) return

  currentStep.value = 2
  if (
    !aiSetupData.value.picture.pictureUrl ||
    !aiSetupData.value.picture.pictureUrl.includes(`Ai-${aiSetupData.value.gender.toLowerCase()}-`)
  ) {
    resolveAvatarImage(aiSetupData.value.gender, currentAvatarIndex.value)
  }
  avatarHistory.value = [currentAvatarIndex.value]
  avatarHistoryPosition.value = 0
}

const onStepAvatar = () => {
  currentStep.value = 3
}

const onStepConfig = async (hide: () => void) => {
  if (!aiSetupData.value.apiKey.trim()) {
    apiKeyErrorMessage.value = "API Key is required"
    return
  }

  isSaving.value = true

  const isValid = await store.dispatch(Actions.AI_CONFIG_TOKEN_CHECK, {
    provider: aiSetupData.value.provider,
    apiKey: aiSetupData.value.apiKey
  })

  if (isValid === false || isValid === "false") {
    apiKeyErrorMessage.value = "Invalid API Key"
    isSaving.value = false
    return
  }

  const isSaved = aiSetupData.value.id
    ? await store.dispatch(Actions.PUT_AI_CONFIG, { aiConfig: aiSetupData.value })
    : await store.dispatch(Actions.POST_AI_CONFIG, { aiConfig: aiSetupData.value })

  if (!isSaved) {
    isSaving.value = false
    return
  }

  isSaving.value = false
  hide()
  shakeAnimation("#aiProfile", 1)
}

const onAction = async (validate: () => Promise<{ valid: boolean }>, hide: () => void) => {
  switch (currentStep.value) {
    case 1:
      return onStepName(validate)
    case 2:
      return onStepAvatar()
    case 3:
      return onStepConfig(hide)
  }
}

const onGetProviders = async () => {
  await store.dispatch(Actions.GET_AI_CONFIG)

  aiSetupData.value = new AIConfigCurrent(currentAiConfig.value)
  if (currentAiConfig.value.id) {
    aiSetupData.value = new AIConfigCurrent(currentAiConfig.value)
    return
  }
  const firstProvider = providers.value[0]
  if (firstProvider) {
    aiSetupData.value.provider = firstProvider.provider
    aiSetupData.value.model =
      firstProvider.models.find(model => model.enabled) ?? new AIConfigModels()
  }
}

const onSelectProvider = (value: AIConfigProvider) => {
  aiSetupData.value.provider = value.provider
  aiSetupData.value.model = value.models.find(model => model.enabled) ?? new AIConfigModels()
}

const onSelectModel = (value: AIConfigModels) => {
  aiSetupData.value.model = value
}

const onApiKeyChange = (value: string) => {
  aiSetupData.value.apiKey = value
  apiKeyErrorMessage.value = ""
}

const resolveAvatarImage = (gender: string, index: number) => {
  aiSetupData.value.picture.pictureUrl = `${s3_base_url}/picture/ai/Ai-${gender.toLowerCase()}-${index}.png`
}

const getRandomAvatarIndex = (excludeIndex: number) => {
  if (maxNumberOfAvatars <= 1) return 1

  let randomIndex = excludeIndex
  while (randomIndex === excludeIndex) {
    randomIndex = Math.floor(Math.random() * maxNumberOfAvatars) + 1
  }

  return randomIndex
}

function goPrevAvatar() {
  if (avatarHistoryPosition.value === -1) {
    avatarHistoryPosition.value = avatarHistory.value.lastIndexOf(currentAvatarIndex.value)
  }

  const previousIndex = avatarHistory.value[avatarHistoryPosition.value - 1]
  if (previousIndex) {
    avatarHistoryPosition.value -= 1
    currentAvatarIndex.value = previousIndex
  } else {
    currentAvatarIndex.value = getRandomAvatarIndex(currentAvatarIndex.value)
    avatarHistory.value.unshift(currentAvatarIndex.value)
    avatarHistoryPosition.value = 0
  }

  resolveAvatarImage(aiSetupData.value.gender, currentAvatarIndex.value)
}

function goNextAvatar() {
  if (avatarHistoryPosition.value === -1) {
    avatarHistoryPosition.value = avatarHistory.value.lastIndexOf(currentAvatarIndex.value)
  }

  const nextIndex = avatarHistory.value[avatarHistoryPosition.value + 1]
  if (nextIndex) {
    avatarHistoryPosition.value += 1
    currentAvatarIndex.value = nextIndex
  } else {
    currentAvatarIndex.value = getRandomAvatarIndex(currentAvatarIndex.value)
    avatarHistory.value.push(currentAvatarIndex.value)
    avatarHistoryPosition.value = avatarHistory.value.length - 1
  }

  resolveAvatarImage(aiSetupData.value.gender, currentAvatarIndex.value)
}

async function onOpened() {
  await onGetProviders()

  const match = aiSetupData.value.picture.pictureUrl?.match(/Ai-\w+-(\d+)\.png$/)
  if (match) {
    currentAvatarIndex.value = Number(match[1]) ?? 0
  }
}

function onClosed() {
  resetData()
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/utils/variables/colors";
@import "@/assets/scss/utils/variables/fonts";
@import "@/assets/scss/utils/mixins/_flex";
@import "@/assets/scss/utils/typography-v2";

:deep(.modal-ai-setup) {
  width: 832px;
  height: 496px;

  @include flex($direction: column, $align: unset);

  @media (max-width: 1536px) {
    width: 648px;
    height: 392px;
  }

  @media (max-width: 1280px) {
    width: 576px;
  }
}

:deep(.modal-ai-setup .custom-modal__body) {
  height: 100%;
  margin-bottom: var(--step6);

  @media (max-width: 1536px) {
    margin-bottom: var(--step5);
  }
}

.modal-ai-setup {
  &__header {
    @include flex($justify: space-between, $align: center);

    padding: var(--step4) var(--step6);
    border-radius: var(--radius-modal) var(--radius-modal) 0 0;
    background: var(--gradient-popup-header);

    @media (max-width: 1536px) {
      padding: var(--step3) var(--step5);
    }
  }

  &__title {
    margin: 0;
    color: var(--monochrome-07);
  }

  &__close {
    @include flex($justify: center, $align: center);

    padding: 0;
    border: none;
    background: transparent;
    color: var(--monochrome-05);
    cursor: pointer;

    &:hover {
      color: var(--monochrome-06);
    }
  }

  &__content {
    @include flex($justify: center, $align: unset);

    height: 100%;
  }

  &__footer {
    @include flex($justify: center, $align: center);

    padding: 0 var(--step6) var(--step6);
  }

  &__control-buttons {
    margin: 0 auto;
  }
}

.dark {
  .modal-ai-setup {
    &__close {
      color: var(--monochrome-04);

      &:hover {
        color: var(--monochrome-03);
      }
    }
  }
}
</style>
